import { useEffect } from 'react';

import {
  IconExclamationTriangleOutline,
  IconFileWithItemsOutline,
} from '@iwoca/icons';
import { Accordion } from '@iwoca/orion';

import styles from './BankStatementUploadRevamp.module.css';
import { useGetDocuments } from '../../../../api/lendingApi/lendingApiQueries';
import { useContextStateKey } from '../../../../hooks/useContextStateKey';
import {
  useFileUploadReducer,
  TFile,
} from '../../../../hooks/useFileUploadReducer';
import UploadedFiles from '../../UploadedFiles/UploadedFiles';
import DragDropUpload from '../DragDropUpload/DragDropUpload';

const DOCUMENT_TYPE = 'bank statement';

type TFilesByStatus = {
  uploaded: TFile[];
  rejected: TFile[];
};

const groupFilesByStatus = (files: TFile[]): TFilesByStatus => {
  return files.reduce<TFilesByStatus>(
    (acc, file) => {
      if (file.status === 'error' || file.status === 'rejected') {
        acc.rejected.push(file);
      } else if (file.status !== 'loading') {
        acc.uploaded.push(file);
      }
      return acc;
    },
    { uploaded: [], rejected: [] },
  );
};

const listOfInstructions = [
  'You can download these from your online banking. See instructions.',
  'Files must be PDF or JPG, JPEG, PNG and no more than 2GB.',
  'These should be for the account(s) where the majority of your business revenue is generated.',
  'Please ensure the statements display the business name, sort code, account number and the account balance.',
];

const BankStatementUploadRevamp = () => {
  const stateKey = useContextStateKey();
  const { documents } = useGetDocuments(stateKey);

  const {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  } = useFileUploadReducer();

  useEffect(() => {
    if (documents) {
      const bankStatementDocuments = documents.data.filter(
        (document) => document.attributes.document_type === DOCUMENT_TYPE,
      );
      setAllFiles(bankStatementDocuments);
    }
  }, [documents]);

  const groupedFiles = groupFilesByStatus(uploadedFiles);
  const hasUploadedFiles = groupedFiles.uploaded.length > 0;
  const hasRejectedFiles = groupedFiles.rejected.length > 0;

  return (
    <div className={styles.container}>
      <Accordion
        title="If you prefer, you can upload your latest 6 months of bank statements (PDF)"
        small
        variant="secondary"
        data-testid="bank-statement-upload-accordion"
        titleClassName={styles.accordionHeader}
      >
        <DragDropUpload
          documentType={DOCUMENT_TYPE}
          listOfInstructions={listOfInstructions}
          setFileLoading={setFileLoading}
          setFileSuccess={setFileSuccess}
          setFileError={setFileError}
          setFileReject={setFileReject}
        />
      </Accordion>

      {hasUploadedFiles && (
        <div className={styles.uploadedSection}>
          <div className={styles.filesList}>
            <UploadedFiles
              files={groupedFiles.uploaded}
              removeFile={removeFile}
            />
          </div>
          <div className={styles.sectionLabel}>
            <IconFileWithItemsOutline className={styles.uploadedIcon} />
            <p>Documents uploaded</p>
          </div>
        </div>
      )}

      {hasRejectedFiles && (
        <div className={styles.rejectedSection}>
          <div className={styles.filesList}>
            <UploadedFiles
              files={groupedFiles.rejected}
              removeFile={removeFile}
            />
          </div>
          <div className={styles.sectionLabelRejected}>
            <IconExclamationTriangleOutline className={styles.rejectedIcon} />
            <p>Documents rejected</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankStatementUploadRevamp;
